import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as medicationsRefills from '../../../redux/healthboard/patientPortal/medication-refills-history';
import * as labTestResults from '../../../redux/healthboard/patientPortal/lab-test-results-req';
import * as socialHistory from '../../../redux/healthboard/patientPortal/social-history-req';
import * as immunizations from '../../../redux/healthboard/patientPortal/immunizations-req';
import * as familyHistory from '../../../redux/healthboard/patientPortal/family-history';
import * as activityLog from '../../../redux/healthboard/patientPortal/activity-log-req';
import * as conditions from '../../../redux/healthboard/patientPortal/conditions-req';
import * as encounters from '../../../redux/healthboard/patientPortal/encounters-req';
import * as procedures from '../../../redux/healthboard/patientPortal/procedures-req';
import * as allergies from '../../../redux/healthboard/patientPortal/allergies-req';
import {
  combinedPendingDataRequest,
  selectAllergiesEntity,
  selectImmunizationEntity,
  selectConditionsEntity,
  selectLabTestResultsEntity,
  selectFamilyHistoryEntity,
  selectEncountersEntity,
  selectSocialHistoryEntity,
  selectActivityLogEntity,
  selectProceduresEntity,
} from '../../../redux/healthboard/patientPortal/selectors';
import { TABS_PATIENT_PORTAL } from '../constants';

const {
  CONDITIONS,
  ALLERGIES,
  LAB_TEST_RESULT,
  IMMUNIZATIONS,
  MEDICATIONS,
  FAMILY_HISTORY,
  ENCOUNTERS,
  SOCIAL_HISTORY,
  HEALTH_RECORD,
  ACTIVITY_LOG,
  PROCEDURES,
  VITALS_AND_ACTIVITY,
} = TABS_PATIENT_PORTAL;

export function useDataListEntity({
  userId,
  activeTab,
  setActiveTab,
  firstCategoryType,
}) {
  const dispatch = useDispatch();
  const pending = useSelector(combinedPendingDataRequest);
  const labTestResultsData = useSelector(selectLabTestResultsEntity);
  const allergiesData = useSelector(selectAllergiesEntity);
  const immunizationsData = useSelector(selectImmunizationEntity);
  const conditionsData = useSelector(selectConditionsEntity);
  const familyHistoryData = useSelector(selectFamilyHistoryEntity);
  const encountersData = useSelector(selectEncountersEntity);
  const socialHistoryData = useSelector(selectSocialHistoryEntity);
  const activityLogData = useSelector(selectActivityLogEntity);
  const proceduresData = useSelector(selectProceduresEntity);

  useEffect(() => {
    clearCurrentEntity();
  }, [userId]);

  useEffect(() => {
    if (activeTab === MEDICATIONS) {
      dispatch(medicationsRefills.actions.send({ userId }));
    }
  }, [activeTab, userId]);

  const chooseDataByType = useMemo(
    () => type => {
      switch (type) {
        case ACTIVITY_LOG:
          return {
            data: activityLogData,
            action: activityLog.actions.loadMore,
            clear: activityLog.actions.clear,
            withPagination: true,
            pending,
          };
        case ALLERGIES:
          return {
            data: allergiesData,
            action: allergies.actions.send,
            clear: allergies.actions.clear,
            pending,
          };
        case IMMUNIZATIONS:
          return {
            data: immunizationsData,
            action: immunizations.actions.send,
            clear: immunizations.actions.clear,
            pending,
          };
        case CONDITIONS:
          return {
            data: conditionsData,
            action: conditions.actions.send,
            clear: conditions.actions.clear,
            pending,
          };
        case LAB_TEST_RESULT:
          return {
            data: labTestResultsData,
            action: labTestResults.actions.send,
            clear: labTestResults.actions.clear,
            pending,
          };
        case FAMILY_HISTORY:
          return {
            data: familyHistoryData,
            action: familyHistory.actions.send,
            clear: familyHistory.actions.clear,
            pending,
          };
        case ENCOUNTERS:
          return {
            data: encountersData,
            action: encounters.actions.send,
            clear: encounters.actions.clear,
            pending,
          };
        case SOCIAL_HISTORY:
          return {
            data: socialHistoryData,
            action: socialHistory.actions.send,
            clear: socialHistory.actions.clear,
            pending,
          };
        case PROCEDURES:
          return {
            data: proceduresData,
            action: procedures.actions.send,
            clear: procedures.actions.clear,
            pending,
          };
        default:
          return {
            data: activityLogData,
            action: activityLog.actions.loadMore,
            clear: activityLog.actions.clear,
            withPagination: true,
            pending,
          };
      }
    },
    [
      activeTab,
      allergiesData,
      labTestResultsData,
      conditionsData,
      immunizationsData,
      socialHistoryData,
      proceduresData,
      pending,
      userId,
    ],
  );

  const loadMore = useCallback(
    (params = {}) => {
      const currentEntity = chooseDataByType(activeTab);
      if (!currentEntity.withPagination || !currentEntity.action || pending)
        return;

      dispatch(
        currentEntity.action({ userId, withPagination: true, ...params }),
      );
    },
    [activeTab, userId, pending],
  );

  const clearCurrentEntity = () => {
    const currentEntity = chooseDataByType(activeTab);
    dispatch(currentEntity.clear());
  };

  const handleSelectTab = useCallback(
    tab => {
      clearCurrentEntity();

      const currentEntity = chooseDataByType(tab);

      setActiveTab(tab);

      if (![HEALTH_RECORD, VITALS_AND_ACTIVITY, MEDICATIONS].includes(tab)) {
        dispatch(
          currentEntity.action({
            userId,
          }),
        );
      }
    },
    [userId],
  );

  return {
    ...chooseDataByType(activeTab || firstCategoryType),
    loadMore,
    handleSelectTab,
  };
}



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/PatientPortal/useDataListEntity.js